import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { gql } from "graphql-request";
import type { Game } from "@/types";
import { GameCard } from "@/components/games/GameCard";
import { GameCardSkeleton } from "@/components/games/GameCardSkeleton";
import { SearchBar } from "@/components/search/SearchBar";
import { usePageTitle } from "@/hooks/usePageTitle";
import { getBootstrapTrackerStats } from "@/lib/bootstrap";
import { getShopLogoUrl } from "@/utils/shopLogos";

const HOME_QUERY = gql`
  query Home {
    trackerStats {
      gamesTracked
      shopsTracked
      priceChangesThisWeek
      latestChangeAt
      totalGames
    }
    gamesWithBiggestDrops(limit: 8) {
      id
      slug
      title
      imageUrl
      thumbnailUrl
      playerRange
      playtimeRange
      displayRating
      ratingSource
      activeListingsCount
      lowestPrice
      priceSparkline
      dropPercentage
      trackedShopCount
      priceHistorySummary {
        allTimeLow
        last30dLow
        currentLow
        currentLowShopName
        trackingSince
      }
    }
    gamesAtAllTimeLow(limit: 6) {
      id
      slug
      title
      imageUrl
      thumbnailUrl
      playerRange
      playtimeRange
      displayRating
      ratingSource
      activeListingsCount
      lowestPrice
      priceSparkline
      dropPercentage
      trackedShopCount
      priceHistorySummary {
        allTimeLow
        last30dLow
        currentLow
        currentLowShopName
        trackingSince
      }
    }
    games(limit: 8, sortBy: "popular") {
      id
      slug
      title
      imageUrl
      thumbnailUrl
      playerRange
      playtimeRange
      displayRating
      ratingSource
      activeListingsCount
      lowestPrice
      priceSparkline
      dropPercentage
      trackedShopCount
      priceHistorySummary {
        allTimeLow
        last30dLow
        currentLow
        currentLowShopName
        trackingSince
      }
    }
    availableShops
  }
`;

interface TrackerStats {
  gamesTracked: number;
  shopsTracked: number;
  priceChangesThisWeek: number;
  latestChangeAt: string | null;
  totalGames: number;
}

interface SectionProps {
  title: string;
  subtitle?: string;
  games: Game[];
  ctaTo?: string;
  ctaLabel?: string;
  // While true and no games have arrived yet, render skeleton cards instead of
  // hiding the section — keeps the layout stable on a cold-cache first paint.
  loading?: boolean;
  skeletonCount?: number;
}

function GamesSection({
  title,
  subtitle,
  games,
  ctaTo,
  ctaLabel,
  loading = false,
  skeletonCount = 8,
}: SectionProps) {
  const showSkeletons = loading && games.length === 0;
  if (games.length === 0 && !showSkeletons) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
        </div>
        {ctaTo && (
          <Link
            to={ctaTo}
            className="shrink-0 text-sm font-medium text-primary-800 hover:text-primary-700"
          >
            {ctaLabel} &rarr;
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
        {showSkeletons
          ? Array.from({ length: skeletonCount }).map((_, i) => (
              <GameCardSkeleton key={i} />
            ))
          : games.map((game) => <GameCard key={game.id} game={game} />)}
      </div>
    </section>
  );
}

// Dynamic teasers replace the static "Tracking 14148 games" stat strip.
// Each teaser is a concrete, *today*-flavored signal the visitor can act on.
function useTeasers(
  stats: TrackerStats | undefined,
  allTimeLowCount: number,
): string[] {
  const { t } = useTranslation();
  if (!stats) return [];
  const items: string[] = [];
  if (allTimeLowCount > 0) {
    items.push(t("home.teaser_lowest", { count: allTimeLowCount }));
  }
  if (stats.priceChangesThisWeek > 0) {
    items.push(
      t("home.teaser_drops", { count: stats.priceChangesThisWeek }),
    );
  }
  if (stats.shopsTracked > 0) {
    items.push(t("home.teaser_shops", { count: stats.shopsTracked }));
  }
  return items;
}

function useLastUpdatedLabel(latestChangeAt: string | null | undefined): string | null {
  const { t } = useTranslation();
  if (!latestChangeAt) return null;
  const minutesAgo = Math.max(
    0,
    Math.floor((Date.now() - new Date(latestChangeAt).getTime()) / 60000),
  );
  if (minutesAgo < 2) return t("home.trust_updated_just_now");
  if (minutesAgo < 60) return t("home.trust_updated_minutes_ago", { count: minutesAgo });
  const hoursAgo = Math.floor(minutesAgo / 60);
  return t("home.trust_updated_hours_ago", { count: hoursAgo });
}

export function HomePage() {
  const { t } = useTranslation();
  usePageTitle();

  const { data, isPending } = useQuery({
    queryKey: ["home"],
    queryFn: () =>
      graphqlClient.request<{
        trackerStats: TrackerStats;
        gamesWithBiggestDrops: Game[];
        gamesAtAllTimeLow: Game[];
        games: Game[];
        availableShops: string[];
      }>(HOME_QUERY),
  });

  const stats = data?.trackerStats ?? getBootstrapTrackerStats();
  const biggestDrops = data?.gamesWithBiggestDrops ?? [];
  const allTimeLows = data?.gamesAtAllTimeLow ?? [];
  const popular = data?.games ?? [];
  const shops = data?.availableShops ?? [];

  const teasers = useTeasers(stats, allTimeLows.length);
  const lastUpdated = useLastUpdatedLabel(stats?.latestChangeAt);

  return (
    <div>
      {/* Hero — search-led, dynamic teasers, secondary deals CTA */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-900 to-primary-950 pb-10 pt-12 text-white sm:pb-14 sm:pt-16">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            {t("home.tracker_title_main")}
            <br />
            <span className="text-primary-300">
              {t("home.tracker_title_accent")}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-primary-200 sm:text-base">
            {t("home.tracker_subtitle")}
          </p>

          <div className="mx-auto mt-6 max-w-2xl">
            <SearchBar size="lg" placeholder={t("home.search_placeholder")} />
          </div>

          {/* Secondary CTA */}
          <div className="mt-4 flex items-center justify-center">
            <Link
              to="/games?sort=biggest_drop"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 17l-5-5m0 0l5-5m-5 5h12"
                  transform="rotate(180 12 12)"
                />
              </svg>
              {t("home.deals_cta")}
            </Link>
          </div>

          {/* Dynamic teasers */}
          {teasers.length > 0 && (
            <ul className="mx-auto mt-7 grid max-w-3xl gap-2 border-t border-primary-700/50 pt-5 text-sm text-primary-100 sm:grid-cols-3 sm:gap-4">
              {teasers.map((teaser, i) => (
                <li
                  key={i}
                  className="flex items-center justify-center gap-1.5"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-300" />
                  {teaser}
                </li>
              ))}
            </ul>
          )}

          {lastUpdated && (
            <p className="mt-3 text-xs text-primary-300/80">{lastUpdated}</p>
          )}
        </div>
      </section>

      {/* Trust signal: shop logos strip */}
      {shops.length > 0 && (
        <section className="border-b border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t("home.trust_shops_heading")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-10">
              {shops.slice(0, 12).map((shop) => {
                const logo = getShopLogoUrl(shop);
                return logo ? (
                  <img
                    key={shop}
                    src={logo}
                    alt={shop}
                    title={shop}
                    className="h-8 w-auto opacity-70 grayscale transition hover:opacity-100 hover:grayscale-0"
                  />
                ) : (
                  <span
                    key={shop}
                    className="text-sm font-medium text-gray-500"
                  >
                    {shop}
                  </span>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <GamesSection
        title={t("home.biggest_drops_title")}
        subtitle={t("home.biggest_drops_subtitle")}
        games={biggestDrops}
        ctaTo="/games?sort=biggest_drop"
        ctaLabel={t("home.deals_cta")}
        loading={isPending}
        skeletonCount={8}
      />

      {(allTimeLows.length > 0 || isPending) && (
        <div className="bg-rose-50/60">
          <GamesSection
            title={t("home.all_time_low_title")}
            subtitle={t("home.all_time_low_subtitle")}
            games={allTimeLows}
            ctaTo="/games?sort=all_time_low"
            ctaLabel={t("home.all_games")}
            loading={isPending}
            skeletonCount={6}
          />
        </div>
      )}

      <div className="bg-gray-50">
        <GamesSection
          title={t("home.popular_title")}
          games={popular}
          ctaTo="/games"
          ctaLabel={t("home.all_games")}
          loading={isPending}
          skeletonCount={8}
        />
      </div>

      {/* Marketplace secondary surface — kept compact, never the lead */}
      <section className="py-12">
        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white px-6 py-8 text-center">
          <p className="text-sm font-medium uppercase tracking-wider text-gray-500">
            {t("home.marketplace_kicker")}
          </p>
          <h2 className="mt-2 text-xl font-semibold text-gray-900">
            {t("home.marketplace_title")}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">
            {t("home.marketplace_subtitle")}
          </p>
          <Link
            to="/listings/new"
            className="btn-primary mt-5 inline-block px-6 py-2.5"
          >
            {t("home.marketplace_cta")}
          </Link>
        </div>
      </section>
    </div>
  );
}
